import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  HomeIcon,
  ArrowLeftIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

interface NotFoundProps {
  title?: string;
  message?: string;
  backPath?: string;
  backLabel?: string;
}

const NotFound: React.FC<NotFoundProps> = ({
  title = "Page Not Found",
  message = "We can't seem to find the page you're looking for.",
  backPath = "/dashboard",
  backLabel = "Go to Dashboard"
}) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-2 px-4 sm:px-6 lg:px-8" style={{ marginTop: '-7%' }}>
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col lg:flex-row items-center justify-center lg:justify-between">
          {/* Left side - Text content */}
          <div className="flex-1 text-center lg:text-left lg:pr-16 mb-8 lg:mb-0">
            <h1 className="text-6xl lg:text-8xl font-bold text-gray-800 mb-4">
              Oops!
            </h1>
            
            <h2 className="text-2xl lg:text-3xl font-semibold text-gray-700 mb-4">
              {title}
            </h2>
            
            <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto lg:mx-0">
              {message}
            </p>

            <div className="text-sm text-gray-500 mb-8">
              <span className="font-medium">Error code: 404</span>
            </div>
            
            {/* Helpful links */}
            <div className="mb-8">
              <p className="text-sm text-gray-600 mb-4">Here are some helpful links instead:</p>
              <ul className="space-y-2 text-left">
                <li>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="text-teal-600 hover:text-teal-700 hover:underline flex items-center"
                  >
                    <HomeIcon className="h-4 w-4 mr-2" />
                    Home
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/incidents')}
                    className="text-teal-600 hover:text-teal-700 hover:underline flex items-center"
                  >
                    <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                    Search Incidents
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate(backPath)}
                    className="text-teal-600 hover:text-teal-700 hover:underline flex items-center"
                  >
                    <ArrowLeftIcon className="h-4 w-4 mr-2" />
                    {backLabel}
                  </button>
                </li>
              </ul>
            </div>

            {/* Primary Actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 rounded-lg text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                Go Back
              </button>
              
              <button
                onClick={() => navigate('/dashboard')}
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-lg text-base font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-colors"
              >
                <HomeIcon className="h-5 w-5 mr-2" />
                Go Home
              </button>
            </div>
          </div>

          {/* Right side - Illustration */}
          <div className="flex-shrink-0">
            <div className="relative">
              {/* Simple illustration using CSS */}
              <div className="w-64 h-64 lg:w-80 lg:h-80 relative">
                {/* Background circle */}
                <div className="absolute inset-0 bg-gradient-to-br from-teal-100 to-teal-200 rounded-full opacity-50"></div>
                
                {/* Character body */}
                <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2">
                  <div className="w-20 h-32 bg-teal-500 rounded-t-full rounded-b-lg relative">
                    {/* Face */}
                    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 w-16 h-16 bg-pink-200 rounded-full">
                      <div className="absolute top-4 left-3 w-2 h-2 bg-pink-400 rounded-full"></div>
                      <div className="absolute top-4 right-3 w-2 h-2 bg-pink-400 rounded-full"></div>
                      <div className="absolute top-7 left-1/2 transform -translate-x-1/2 w-3 h-1 bg-pink-400 rounded-full"></div>
                    </div>
                    
                    {/* Hair */}
                    <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 w-20 h-8 bg-teal-700 rounded-t-full"></div>
                    
                    {/* Arms */}
                    <div className="absolute top-6 -left-6 w-6 h-16 bg-pink-200 rounded-full transform rotate-12"></div>
                    <div className="absolute top-6 -right-6 w-6 h-16 bg-pink-200 rounded-full transform -rotate-12"></div>
                    
                    {/* Buttons */}
                    <div className="absolute top-8 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-purple-600 rounded-full"></div>
                    <div className="absolute top-12 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-purple-600 rounded-full"></div>
                    <div className="absolute top-16 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-purple-600 rounded-full"></div>
                  </div>
                  
                  {/* Legs */}
                  <div className="absolute top-32 left-1/2 transform -translate-x-1/2 flex space-x-2">
                    <div className="w-6 h-16 bg-purple-600 rounded-lg"></div>
                    <div className="w-6 h-16 bg-purple-600 rounded-lg"></div>
                  </div>
                  
                  {/* Shoes */}
                  <div className="absolute top-44 left-1/2 transform -translate-x-1/2 flex space-x-2">
                    <div className="w-8 h-4 bg-teal-700 rounded-full"></div>
                    <div className="w-8 h-4 bg-teal-700 rounded-full"></div>
                  </div>
                  
                  {/* Paper/Document in hand */}
                  <div className="absolute top-4 -right-8 w-4 h-6 bg-yellow-300 rounded-sm transform rotate-12"></div>
                </div>
                
                {/* Shadow */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 w-24 h-4 bg-gray-300 rounded-full opacity-40"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;